import React from 'react';
import clsx from 'clsx';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';

import { Task } from '@/src/components/myTasks2/components/Tabs/MyPortfolio/types/task';
import StatusIcon from '@/src/components/project/Processes2/components/Table/Elements/TaskName/StatusIcon';
import TaskStatusPill from '@/src/components/project/Common/TaskStatusPill';
import { getData, showToast } from '@/src/globalFunction';
import { useMarkTaskAsComplete } from '@/src/components/project/Processes2/api/markTaskAsComplete';
import { useMarkTaskAsUncomplete } from '@/src/components/project/Processes2/api/markTaskAsUncomplete';
import Icon, {
  ICON_TYPES,
  ICONS,
} from '@/src/components/common/Elements/Icon/Icon';
import Tooltip from '@/src/components/common/Elements/Tooltip/Tooltip';
import Assignee from './Assignee';

import { renderBorderColor, parseDate } from '../utils';
import { invalidateMyTasksDashboard } from '@/src/components/myTasks2/helper';
import { useMyPortfolio } from '@/src/components/myTasks2/components/Tabs/MyPortfolio/store/myPortfolio.store';

interface WorkspaceTaskEntryProps {
  task: Task;
  compactWidth?: boolean;
}

export const WorkspaceTaskEntryHeader: React.FC = () => (
  <div
    className={clsx(
      'tw-flex',
      'tw-flex-row',
      'tw-items-center',
      'tw-py-2',
      'tw-px-4',
      'tw-gap-6',
      'tw-border-b',
      'tw-font-semibold',
      'tw-min-w-max',
      'tw-text-gray-700',
      'tw-sticky',
      'tw-top-0',
      'tw-z-100',
      'tw-bg-white'
    )}
    style={{ minHeight: 40 }}
  >
    <div className={clsx('tw-flex-1', 'tw-min-w-[300px]', 'tw-text-left')}>
      Task Name
    </div>
    <div className={clsx('tw-flex-1', 'tw-min-w-[200px]', 'tw-text-center')}>
      Action
    </div>
    <div className={clsx('tw-flex-1', 'tw-min-w-[150px]', 'tw-text-center')}>
      Adjusted Date
    </div>
    <div className={clsx('tw-min-w-[40px]', 'tw-text-center')}></div>
  </div>
);

const WorkspaceTaskEntry: React.FC<WorkspaceTaskEntryProps> = ({
  task,
  compactWidth,
}) => {
  const borderColor = renderBorderColor(task.taskStatus);
  const containerWidth = compactWidth ? '100%' : undefined;
  const dispatch = useDispatch();
  const { resetSocketData } = useMyPortfolio();

  const handleTaskClick = () => {
    return getData(`/api/task/detail/${task.taskId}`, {}, (c) => {
      if (c && c.status === 200 && !c.data.error) {
        const task = c.data;
        dispatch({ type: 'SET_TASK_SELECTED', Selected: task });
        dispatch({ type: 'SET_TASK_LOADING', Loading: '' });
        dispatch({ type: 'SET_SCREEN_LOADER', Loading: false });
        $(`#task-details`).modal('show');
      } else {
        showToast('error', c.data.message);
      }
    });
  };

  // Mark as complete logic (mutation + cache invalidation)
  const handleMarkAsComplete = async () => {
    invalidateMyTasksDashboard();
    resetSocketData();
  };

  // Mark as uncomplete logic
  const handleMarkAsUncomplete = async () => {
    invalidateMyTasksDashboard();
    resetSocketData();
  };

  const renderDueDate = () => {
    if (!task.dueDate) return;
    const dateObj =
      typeof task.dueDate === 'string' ? new Date(task.dueDate) : task.dueDate;

    let isAdjustedPast = false;
    if (task.adjustedDate) {
      const adjustedDateObj = parseDate(task.adjustedDate, true);
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      if (adjustedDateObj && adjustedDateObj < today) {
        isAdjustedPast = true;
      }
    }

    const isDelayed = task.taskStatus === 'Delayed';
    const textColor =
      isDelayed || isAdjustedPast ? 'tw-text-cfo-red-400' : 'tw-text-cfo-black';

    return (
      <span
        className={clsx(
          textColor,
          'tw-whitespace-nowrap',
          'tw-bg-[##FBF1F0]',
          'tw-rounded-[8px]',
          'tw-px-[4px]',
          'tw-py-[2px]',
          'tw-min-w-[100px]',
          'tw-max-w-[100px]',
          'tw-text-[12px]'
        )}
      >
        {dateObj ? (
          dateObj.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
          })
        ) : (
          <div
            className={clsx(
              'tw-min-w-[100px]',
              'tw-max-w-[100px]',
              'tw-bg-red-500'
            )}
          >
            {' '}
            -{' '}
          </div>
        )}
      </span>
    );
  };

  return (
    <>
      <Tooltip
        id="general_process_list_tooltip"
        place="bottom"
        effect="solid"
        delayHide={0}
        eventOff="mouseleave"
      >
        <div className="tw-flex tw-items-center" style={{ gap: '4px' }}>
          <Icon icon={ICONS.info} iconType={ICON_TYPES.outlined} />
          <span>Requires review/approval process</span>
        </div>
      </Tooltip>
      {compactWidth ? (
        <div
          className={clsx(
            'tw-flex',
            'tw-flex-row',
            'tw-items-stretch',
            'tw-bg-white',
            'tw-rounded-lg',
            'tw-shadow-sm',
            'tw-py-3',
            'tw-px-4',
            'tw-gap-4',
            'tw-border',
            'tw-mb-2'
          )}
          onClick={handleTaskClick}
          style={{
            minHeight: 80,
            cursor: 'pointer',
            borderLeft: borderColor ? `6px solid ${borderColor}` : undefined,
            width: containerWidth,
          }}
        >
          {/* First column: Status Icon, vertically centered */}
          <div
            className={clsx(
              'tw-flex',
              'tw-items-center',
              'tw-justify-center',
              'tw-w-12',
              'tw-h-full',
              'tw-align-center'
            )}
            style={{ alignSelf: 'center' }}
          >
            <StatusIcon
              task={task}
              markAsComplete={handleMarkAsComplete}
              markAsUncomplete={handleMarkAsUncomplete}
            />
          </div>
          {/* Second column: 3 rows */}
          <div
            className={clsx(
              'tw-flex',
              'tw-flex-col',
              'tw-gap-y-2',
              'tw-flex-1',
              'tw-h-full'
            )}
          >
            {/* Row 1: Due Date */}
            <div
              className={clsx(
                'tw-text-md',
                'tw-text-gray-500',
                'tw-mb-1',
                'tw-h-4'
              )}
            >
              {renderDueDate()}
            </div>
            {/* Row 2: Task Name, vertically centered with StatusIcon */}
            <div
              className={clsx(
                'tw-flex',
                'tw-items-center',
                'tw-justify-start',
                'tw-h-8',
                'tw-text-md',
                'tw-font-medium',
                'tw-truncate',
                'tw-pt-[8px]'
              )}
              style={{
                maxWidth: 220,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {task.taskName}
            </div>
            {/* Row 3: Assignee and TaskStatusPill */}
            <div
              className={clsx(
                'tw-flex',
                'tw-items-center',
                'tw-gap-2',
                'tw-mt-1',
                'tw-h-6'
              )}
            >
              <Assignee
                hideTags
                task={task}
                data-tooltip-id="assignee_avatar_tooltip"
              />
              <div className="tw-ml-auto">
                {task.isMilestone === true || task.hasReport === true ? (
                  <TaskStatusPill
                    isMilestone={task.isMilestone}
                    hasReport={task.hasReport}
                    isCalendar
                    style={{
                      fontSize: '0.8em',
                      height: '22px',
                      minWidth: '30px',
                      maxWidth: '60px',
                    }}
                  />
                ) : (
                  <div className="tw-min-w-[30px] tw-max-w-[30px]" />
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div
          className={clsx(
            'tw-flex',
            'tw-flex-row',
            'tw-items-center',
            'tw-bg-white',
            'tw-rounded-lg',
            'tw-shadow-sm',
            'tw-py-3',
            'tw-px-4',
            'tw-gap-[2rem]',
            'tw-border',
            'tw-mb-2'
          )}
          onClick={handleTaskClick}
          style={{
            minHeight: 56,
            cursor: 'pointer',
            borderLeft: borderColor ? `6px solid ${borderColor}` : undefined,
            width: containerWidth,
          }}
        >
          {/* Status Icon */}
          <div
            className={clsx(
              'tw-flex',
              'tw-items-center',
              'tw-justify-center',
              'tw-w-8'
            )}
          >
            {task !== undefined && (
              <StatusIcon
                task={task}
                markAsComplete={handleMarkAsComplete}
                markAsUncomplete={handleMarkAsUncomplete}
              />
            )}
          </div>
          {/* Task Name */}
          <div
            className={clsx(
              'tw-flex-1',
              'tw-text-md',
              'tw-truncate',
              'tw-min-w-[120px]',
              'tw-max-w-[220px]'
            )}
            style={{
              maxWidth: 220,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {task.taskName}
          </div>
          {/* Task Status */}
          <TaskStatusPill status={task.taskStatus} task={task} isDashboard />
          {/* Assignee */}
          by
          <div
            className={clsx(
              'tw-flex',
              'tw-items-center',
              'tw-gap-2',
              'tw-min-w-[120px]',
              'tw-max-w-[120px]'
            )}
          >
            <Assignee
              hideTags
              task={task}
              data-tooltip-id="assignee_avatar_tooltip"
            />
          </div>
          {/* Due Date */}
          <div
            className={clsx(
              'tw-text-sm',
              'tw-text-gray-500',
              'tw-min-w-[100px]',
              'tw-max-w-[100px]'
            )}
          >
            {renderDueDate()}
          </div>
          {/** Task Status */}
          {task.isMilestone || task.hasReport ? (
            <div className="tw-min-w-[100px] tw-max-w-[100px]">
              <TaskStatusPill
                isMilestone={task.isMilestone}
                hasReport={task.hasReport}
                isCalendar
                task={task}
                style={{ fontSize: '0.8em', height: '22px' }}
              />
            </div>
          ) : (
            <div className="tw-min-w-[40px] tw-max-w-[40px]" />
          )}
        </div>
      )}
    </>
  );
};

export default WorkspaceTaskEntry;
